import React, {useCallback, useContext, useEffect, useRef, useState} from 'react';
import axios from 'axios';
import {useNavigate} from 'react-router-dom';
import Spinner from 'react-bootstrap/Spinner';
import {API_URL} from '../../constants';
import {AuthContext} from '../AuthContext';
import {AXIOS_NOT_FOUND_REDIRECT_OPT_OUT} from '../../axiosNotFoundRedirect';

const ALLOWED_MIME_TYPES = ['application/pdf', 'image/jpeg', 'image/png'];

const normalizeMimeType = (response) => {
    const blobType = response?.data instanceof Blob ? response.data.type : '';
    const headerType = response?.headers?.['content-type']
        || response?.headers?.get?.('content-type')
        || '';

    return String(headerType || blobType)
        .split(';')[0]
        .trim()
        .toLowerCase();
};

export const getExpectedVehicleInsuranceDownloadUrl = (vehicleId) => {
    const normalizedId = String(vehicleId ?? '');

    if (!/^[1-9]\d*$/.test(normalizedId)) return null;

    return `/api/admin/vehicles/${normalizedId}/insurance-document`;
};

const VehicleInsuranceDocumentPreview = ({
    enabled = true,
    imageAlt = 'Cargo Protection document preview',
    label = 'Cargo Protection',
    metadata,
    vehicleId,
}) => {
    const {token, setToken} = useContext(AuthContext);
    const navigate = useNavigate();
    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [loadError, setLoadError] = useState('');
    const objectUrlRef = useRef(null);
    const requestIdRef = useRef(0);

    const revokeObjectUrl = useCallback(() => {
        if (objectUrlRef.current) {
            window.URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
    }, []);

    useEffect(() => {
        requestIdRef.current += 1;
        const requestId = requestIdRef.current;
        revokeObjectUrl();
        setPreview(null);
        setLoading(false);
        setLoadError('');

        if (!enabled || !token || metadata?.available !== true) return undefined;

        const expectedDownloadUrl = getExpectedVehicleInsuranceDownloadUrl(vehicleId);
        if (!expectedDownloadUrl || metadata?.download_url !== expectedDownloadUrl) {
            setLoadError(`Unable to load ${label} securely.`);
            return undefined;
        }

        const abortController = new AbortController();
        setLoading(true);

        axios.get(`${API_URL}${expectedDownloadUrl}`, {
            ...AXIOS_NOT_FOUND_REDIRECT_OPT_OUT,
            headers: {Authorization: `Bearer ${token}`},
            responseType: 'blob',
            signal: abortController.signal,
        }).then((response) => {
            if (requestIdRef.current !== requestId) return;

            const mimeType = normalizeMimeType(response);
            if (!ALLOWED_MIME_TYPES.includes(mimeType)) {
                setLoadError(`Unable to preview ${label}: unsupported file type.`);
                return;
            }

            const responseBlob = response.data instanceof Blob
                ? response.data
                : new Blob([response.data], {type: mimeType});
            const previewBlob = responseBlob.type === mimeType
                ? responseBlob
                : new Blob([responseBlob], {type: mimeType});
            const objectUrl = window.URL.createObjectURL(previewBlob);

            if (requestIdRef.current !== requestId) {
                window.URL.revokeObjectURL(objectUrl);
                return;
            }

            objectUrlRef.current = objectUrl;
            setPreview({mimeType, url: objectUrl});
        }).catch((error) => {
            if (requestIdRef.current !== requestId) return;

            if (error?.response?.status === 401) {
                localStorage.removeItem('authToken');
                setToken(null);
                navigate('/');
                return;
            }

            setLoadError(`Unable to load ${label} securely.`);
        }).finally(() => {
            if (requestIdRef.current === requestId) setLoading(false);
        });

        return () => {
            abortController.abort();
            requestIdRef.current += 1;
            revokeObjectUrl();
        };
    }, [
        enabled,
        label,
        metadata?.available,
        metadata?.download_url,
        navigate,
        revokeObjectUrl,
        setToken,
        token,
        vehicleId,
    ]);

    if (metadata?.available !== true) return 'Not Uploaded';
    if (!enabled) return null;

    return (
        <div data-testid="vehicle-insurance-document-preview">
            {loading ? (
                <div className="py-3" role="status" aria-label={`Loading ${label}`}>
                    <Spinner animation="border" size="sm"/>
                </div>
            ) : preview ? (
                <>
                    {preview.mimeType === 'application/pdf' ? (
                        <iframe
                            title={`${label} preview`}
                            src={preview.url}
                            style={{width: '100%', minHeight: '300px', border: 0}}
                        />
                    ) : (
                        <img
                            src={preview.url}
                            alt={imageAlt}
                            style={{maxWidth: '100%', height: '300px', objectFit: 'contain'}}
                        />
                    )}
                    <a
                        href={preview.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="btn btn-link btn-sm"
                    >
                        Open {label}
                    </a>
                </>
            ) : loadError ? (
                <div className="alert alert-danger my-2" role="alert">{loadError}</div>
            ) : null}
        </div>
    );
};

export default VehicleInsuranceDocumentPreview;
